import { ApiError, apiFetch, parseErrorDetail } from "@/api/fetch";
import type {
  ExtractionFastPassages,
  CurationColumnInput,
  DatasetMonitor,
  ExtractionCheckpoint,
  ExtractionColumnUpdate,
  ExtractionDataset,
  ExtractionDatasetDeleteResult,
  ExtractionGridObserve,
  ExtractionGridInspect,
  ExtractionGridInspectFilters,
  ExtractionRowsPage,
  ExtractionShare,
} from "../types";

export class ExtractionRequestError extends ApiError {
  constructor(message: string, status: number) {
    super(message, status);
    this.name = "ExtractionRequestError";
  }
}

const extractionBase = (datasetId?: string) =>
  datasetId ? `/extractions/${encodeURIComponent(datasetId)}` : "/extractions";

/** The server leaves a note in the dataset's chat, so that its agent knows of the change. */
const withChatNote = (path: string) => `${path}${path.includes("?") ? "&" : "?"}chat_note=true`;

const jsonRequest = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await apiFetch(path, init);
  if (!response.ok) {
    throw new ExtractionRequestError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<T>;
};

/** The lists poll the datasets, so they ask for them without their columns. */
export const listExtractionDatasets = async (): Promise<ExtractionDataset[]> => {
  const result = await jsonRequest<{ datasets: ExtractionDataset[] }>(
    `${extractionBase()}?view=summary`,
  );
  return result.datasets;
};

/** The page polls the dataset, so it leaves out the count of the sources of its rows. */
export const getExtractionDataset = async (
  datasetId: string,
): Promise<ExtractionDataset> =>
  jsonRequest(`${extractionBase(datasetId)}?source_summary=false`, { method: "GET" });

export const shareExtractionToClipboard = async (
  datasetId: string,
): Promise<ExtractionShare> =>
  jsonRequest(`${extractionBase(datasetId)}/share`, { method: "POST" });

export const deleteExtractionDataset = async (
  datasetId: string,
): Promise<ExtractionDatasetDeleteResult> =>
  jsonRequest(extractionBase(datasetId), { method: "DELETE" });

export const observeExtractionGrid = async (
  datasetId: string,
): Promise<ExtractionGridObserve> =>
  jsonRequest(`${extractionBase(datasetId)}/grid/observe`, { method: "GET" });

export const inspectExtractionCell = async (
  datasetId: string,
  rowId: string,
  columnId: string,
): Promise<ExtractionGridInspect> =>
  jsonRequest(`${extractionBase(datasetId)}/grid/inspect`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      cell_row_id: rowId,
      cell_column_id: columnId,
      evidence: "full",
      limit: 1,
    }),
  });

export const inspectExtractionGrid = async (
  datasetId: string,
  filters: ExtractionGridInspectFilters,
): Promise<ExtractionGridInspect> =>
  jsonRequest(`${extractionBase(datasetId)}/grid/inspect`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      evidence: "concise",
      limit: 200,
      ...filters,
    }),
  });

/**
 * The grid polls its rows, so it asks for the grid view: claims without their
 * payloads, no segment list for a cell of one segment, and no source wording. The
 * evidence dialog loads the full cell.
 */
export const listExtractionRows = async (
  datasetId: string,
  page: number,
  perPage: number,
): Promise<ExtractionRowsPage> => {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
    view: "grid",
  });
  return jsonRequest(`${extractionBase(datasetId)}/rows?${params}`, {
    method: "GET",
  });
};

const gridErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body: unknown = await response.json();
    const detail =
      body && typeof body === "object" ? (body as { detail?: unknown }).detail : undefined;
    const message =
      detail && typeof detail === "object"
        ? (detail as { message?: unknown }).message
        : detail;
    if (typeof message === "string") return message.replace(/^Action \d+ failed: /, "");
  } catch {
    /* ignore */
  }
  return `Request failed (${response.status})`;
};

const gridBatchId = async (response: Response): Promise<string | null> => {
  const body: unknown = await response.json().catch(() => null);
  const batchId =
    body && typeof body === "object" ? (body as { batch_id?: unknown }).batch_id : null;
  return typeof batchId === "string" ? batchId : null;
};

/** Deletes a grid row. Returns the ID of the grid batch, or null when the response has none. */
export const deleteExtractionRow = async (
  datasetId: string,
  rowId: string,
): Promise<string | null> => {
  const response = await apiFetch(`${extractionBase(datasetId)}/grid/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      idempotency_key: `delete-row-${rowId}-${Date.now()}`,
      actions: [{ action: "delete_row", row_id: rowId }],
    }),
  });
  if (!response.ok) {
    throw new ExtractionRequestError(await gridErrorMessage(response), response.status);
  }
  return gridBatchId(response);
};

/**
 * Clears populated cells in one atomic grid request; a changed cell fails the request.
 * Returns the ID of the grid batch, or null when the response has none.
 */
export const clearExtractionCells = async (
  datasetId: string,
  cells: { rowId: string; columnId: string; revision: number }[],
): Promise<string | null> => {
  const response = await apiFetch(`${extractionBase(datasetId)}/grid/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      idempotency_key: `clear-cells-${crypto.randomUUID()}`,
      actions: cells.map((cell) => ({
        action: "clear_cell",
        row_id: cell.rowId,
        column_id: cell.columnId,
        expected_cell_revision: cell.revision,
      })),
    }),
  });
  if (!response.ok) {
    throw new ExtractionRequestError(await gridErrorMessage(response), response.status);
  }
  return gridBatchId(response);
};

/**
 * Leaves a note in the dataset's chat on the cells that a grid batch cleared and
 * the rows that it deleted. While the agent works, the note waits for it to stop.
 */
export const addGridChatNote = async (
  datasetId: string,
  batchId: string,
): Promise<{ saved: boolean }> =>
  jsonRequest(`${extractionBase(datasetId)}/chat/note`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ batch_id: batchId }),
  });

/**
 * Moves the notes that waited while the agent worked into the dataset's chat.
 * `waiting` counts the notes that still wait, because the agent still works.
 */
export const deliverChatNotes = async (
  datasetId: string,
): Promise<{ delivered: number; waiting: number }> =>
  jsonRequest(`${extractionBase(datasetId)}/chat/notes/deliver`, { method: "POST" });

export const createExtractionCheckpoint = async (
  datasetId: string,
  expectedRevision: number,
  message?: string,
): Promise<ExtractionCheckpoint> =>
  jsonRequest(withChatNote(`${extractionBase(datasetId)}/checkpoints`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      expected_revision: expectedRevision,
      ...(message ? { message } : {}),
    }),
  });

export const listExtractionCheckpoints = async (
  datasetId: string,
): Promise<ExtractionCheckpoint[]> => {
  const result = await jsonRequest<{ checkpoints: ExtractionCheckpoint[] }>(
    `${extractionBase(datasetId)}/checkpoints`,
  );
  return result.checkpoints;
};

export const restoreExtractionCheckpoint = async (
  datasetId: string,
  checkpointId: string,
  expectedRevision: number,
): Promise<{ checkpoint: ExtractionCheckpoint; dataset: ExtractionDataset }> =>
  jsonRequest(
    withChatNote(
      `${extractionBase(datasetId)}/checkpoints/${encodeURIComponent(checkpointId)}/restore`,
    ),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expected_revision: expectedRevision }),
    },
  );

export const downloadExtractionCheckpointCsv = async (
  datasetId: string,
  checkpointId: string,
): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await apiFetch(
    `${extractionBase(datasetId)}/checkpoints/${encodeURIComponent(checkpointId)}/export.csv`,
    { method: "GET" },
  );
  if (!response.ok) {
    throw new ExtractionRequestError(await parseErrorDetail(response), response.status);
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = disposition.match(/filename="([^"]+)"/i)?.[1] ?? null;
  return { blob: await response.blob(), filename };
};

export const downloadExtractionCheckpointImages = async (
  datasetId: string,
  checkpointId: string,
): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await apiFetch(
    `${extractionBase(datasetId)}/checkpoints/${encodeURIComponent(checkpointId)}/export.images.zip`,
    { method: "GET" },
  );
  if (!response.ok) {
    throw new ExtractionRequestError(await parseErrorDetail(response), response.status);
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = disposition.match(/filename="([^"]+)"/i)?.[1] ?? null;
  return { blob: await response.blob(), filename };
};

export const replaceExtractionSchema = async (
  datasetId: string,
  expectedRevision: number,
  columns: CurationColumnInput[],
  { chatNote = true }: { chatNote?: boolean } = {},
): Promise<ExtractionDataset> => {
  const path = `${extractionBase(datasetId)}/schema`;
  return jsonRequest(chatNote ? withChatNote(path) : path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ expected_revision: expectedRevision, columns }),
  });
};

/** Adds one optional column to an approved dataset. Its rows and verdicts stay. */
export const addExtractionColumn = async (
  datasetId: string,
  expectedRevision: number,
  column: CurationColumnInput,
  { chatNote = true }: { chatNote?: boolean } = {},
): Promise<ExtractionDataset> => {
  const path = `${extractionBase(datasetId)}/columns`;
  return jsonRequest(chatNote ? withChatNote(path) : path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ expected_revision: expectedRevision, column }),
  });
};

/**
 * Changes the name, type, definition, or required flag of an approved column.
 * It keeps its ID and cells; the result counts the cells that no longer fit.
 */
export const updateExtractionColumn = async (
  datasetId: string,
  columnId: string,
  expectedRevision: number,
  column: Omit<CurationColumnInput, "column_id">,
): Promise<ExtractionColumnUpdate> =>
  jsonRequest(
    withChatNote(`${extractionBase(datasetId)}/columns/${encodeURIComponent(columnId)}`),
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expected_revision: expectedRevision, column }),
    },
  );

/** Deletes one column of an approved dataset, with its cells. Checkpoints keep them. */
export const deleteExtractionColumn = async (
  datasetId: string,
  columnId: string,
  expectedRevision: number,
): Promise<ExtractionDataset> =>
  jsonRequest(
    withChatNote(
      `${extractionBase(datasetId)}/columns/${encodeURIComponent(columnId)}?expected_revision=${expectedRevision}`,
    ),
    { method: "DELETE" },
  );

/**
 * Approves the columns. Without `startCollection`, the collection waits until
 * the user starts it, and a note tells the chat.
 */
export const approveExtractionSchema = async (
  datasetId: string,
  expectedRevision: number,
  { startCollection = true }: { startCollection?: boolean } = {},
): Promise<ExtractionDataset> => {
  const path = `${extractionBase(datasetId)}/schema/approve`;
  return jsonRequest(
    startCollection ? path : withChatNote(`${path}?start_collection=false`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expected_revision: expectedRevision }),
    },
  );
};

/** Ends the wait of columns approved without a start. The page then messages the agent. */
export const startExtractionCollection = async (
  datasetId: string,
): Promise<ExtractionDataset> =>
  jsonRequest(`${extractionBase(datasetId)}/collection/start`, { method: "POST" });

/** Stops the turns that continue the collection, and the turn that runs now. */
export const stopExtractionCollection = async (
  datasetId: string,
): Promise<ExtractionDataset> =>
  jsonRequest(`${extractionBase(datasetId)}/collection/stop`, { method: "POST" });

export const finalizeExtractionDataset = async (
  datasetId: string,
  expectedRevision: number,
): Promise<ExtractionDataset> =>
  jsonRequest(`${extractionBase(datasetId)}/finalize`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ expected_revision: expectedRevision }),
  });

export const downloadExtractionCsv = async (
  datasetId: string,
): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await apiFetch(
    `${extractionBase(datasetId)}/export.csv`,
    { method: "GET" },
  );
  if (!response.ok) {
    throw new ExtractionRequestError(await parseErrorDetail(response), response.status);
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = disposition.match(/filename="([^"]+)"/i)?.[1] ?? null;
  return { blob: await response.blob(), filename };
};

export const downloadExtractionImages = async (
  datasetId: string,
): Promise<{ blob: Blob; filename: string | null }> => {
  const response = await apiFetch(
    `${extractionBase(datasetId)}/export.images.zip`,
    { method: "GET" },
  );
  if (!response.ok) {
    throw new ExtractionRequestError(await parseErrorDetail(response), response.status);
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = disposition.match(/filename="([^"]+)"/i)?.[1] ?? null;
  return { blob: await response.blob(), filename };
};

const monitorPath = (datasetId: string, monitorId: string) =>
  `${extractionBase(datasetId)}/monitors/${encodeURIComponent(monitorId)}`;

export const listDatasetMonitors = async (datasetId: string): Promise<DatasetMonitor[]> => {
  const result = await jsonRequest<{ monitors: DatasetMonitor[] }>(
    `${extractionBase(datasetId)}/monitors`,
  );
  return result.monitors;
};

export const setDatasetMonitorEnabled = async (
  datasetId: string,
  monitorId: string,
  enabled: boolean,
): Promise<DatasetMonitor> =>
  jsonRequest(withChatNote(monitorPath(datasetId, monitorId)), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  });

export const deleteDatasetMonitor = async (
  datasetId: string,
  monitorId: string,
): Promise<void> => {
  const response = await apiFetch(withChatNote(monitorPath(datasetId, monitorId)), {
    method: "DELETE",
  });
  if (!response.ok) {
    throw new ExtractionRequestError(await parseErrorDetail(response), response.status);
  }
};

/** Queues a run now; it starts when the dataset's chat is free. */
export const runDatasetMonitor = async (
  datasetId: string,
  monitorId: string,
): Promise<void> => {
  await jsonRequest(`${monitorPath(datasetId, monitorId)}/run`, { method: "POST" });
};

/**
 * Builds a dataset in Fast mode. Resolves with the dataset as soon as its
 * columns exist; the build goes on while the rest of the response streams, and
 * `onEnd` gets its result. The page polls the dataset for the build's progress.
 */
export const createFastExtraction = async (
  question: string,
  onEnd?: (builder: ExtractionDataset["builder"]) => void,
): Promise<ExtractionDataset> => {
  const response = await apiFetch(`${extractionBase()}/fast`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question }),
  });
  if (!response.ok || !response.body) {
    throw new ExtractionRequestError(await parseErrorDetail(response), response.status);
  }
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  let dataset: ExtractionDataset | null = null;
  const readLine = async (): Promise<Record<string, unknown> | null> => {
    for (;;) {
      const end = buffer.indexOf("\n");
      if (end >= 0) {
        const line = buffer.slice(0, end).trim();
        buffer = buffer.slice(end + 1);
        if (line) return JSON.parse(line) as Record<string, unknown>;
        continue;
      }
      const { value, done } = await reader.read();
      if (done) return null;
      buffer += value;
    }
  };
  const first = await readLine();
  dataset = (first?.dataset as ExtractionDataset | undefined) ?? null;
  if (!dataset) throw new ExtractionRequestError("The dataset builder sent no dataset", 502);
  void (async () => {
    try {
      for (let line = await readLine(); line; line = await readLine()) {
        if ("builder" in line) onEnd?.(line.builder as ExtractionDataset["builder"]);
      }
    } catch {
      // The page polls the dataset; a dropped stream does not stop the build.
    }
  })();
  return dataset;
};

/** The passages of one paper that a Fast dataset's build read; the cell spans index into them. */
export const fastExtractionPassages = async (
  datasetId: string,
  docId: string,
): Promise<ExtractionFastPassages> =>
  jsonRequest(`${extractionBase(datasetId)}/fast/passages?doc_id=${encodeURIComponent(docId)}`);

/** Reads more papers of a Fast dataset's build and adds their rows. */
export const fastExtractionMoreRows = async (datasetId: string): Promise<ExtractionDataset> =>
  jsonRequest(`${extractionBase(datasetId)}/fast/more`, { method: "POST" });

/** Fills added or edited columns of a Fast dataset from the passages its build read. */
export const fastExtractionFillColumns = async (
  datasetId: string,
  columnIds: string[],
): Promise<ExtractionDataset> =>
  jsonRequest(`${extractionBase(datasetId)}/fast/extract`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ column_ids: columnIds }),
  });
